import { useId } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { t } from "../i18n/t";
import { useObrazZaplecza } from "../api/zaplecze";
import { cx, zmienne } from "./klasy";
import { IkonaObraz, IkonaWLewo } from "./ikony";
import type { KomponentIkony } from "./ikony";
import "../styles/wspolne.css";

/* ---------- Karta ---------- */
export interface KartaProps {
  tytul?: string;
  ikona?: KomponentIkony;
  /** Elementy po prawej stronie nagłówka (pigułka, przycisk) */
  akcjeNaglowka?: ReactNode;
  stopka?: ReactNode;
  ciasna?: boolean;
  /** Znacznik HTML: section (domyślnie) albo article dla samodzielnych elementów */
  jako?: "section" | "article" | "div";
  id?: string;
  className?: string;
  children: ReactNode;
}

/** Podstawowa powierzchnia: zaokrąglona karta z opcjonalnym nagłówkiem i stopką. */
export function Karta({ tytul, ikona: Ikona, akcjeNaglowka, stopka, ciasna, jako: Znacznik = "section", id, className, children }: KartaProps) {
  const idTytulu = useId();
  return (
    <Znacznik id={id} className={cx("iot-karta", ciasna && "iot-karta--ciasna", className)} aria-labelledby={tytul ? idTytulu : undefined}>
      {(tytul || akcjeNaglowka) && (
        <header className="iot-karta__naglowek">
          {Ikona && <Ikona className="iot-karta__ikona" />}
          {tytul && <h2 className="iot-karta__tytul" id={idTytulu}>{tytul}</h2>}
          {akcjeNaglowka}
        </header>
      )}
      {children}
      {stopka && <footer className="iot-karta__stopka">{stopka}</footer>}
    </Znacznik>
  );
}

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("iot-panel", className)}>{children}</div>;
}

/* ---------- Nagłówek strony ---------- */
export interface NaglowekStronyProps {
  tytul: ReactNode;
  opis?: ReactNode;
  /** Link „wstecz” nad tytułem */
  wstecz?: { do: string; etykieta: string };
  akcje?: ReactNode;
}

export function NaglowekStrony({ tytul, opis, wstecz, akcje }: NaglowekStronyProps) {
  return (
    <header className="iot-naglowek-strony">
      <div className="iot-naglowek-strony__tytul">
        {wstecz && <Link className="iot-okruszki" to={wstecz.do}><IkonaWLewo />{wstecz.etykieta}</Link>}
        <h1>{tytul}</h1>
        {opis && <div className="iot-naglowek-strony__opis">{opis}</div>}
      </div>
      {akcje && <div className="iot-naglowek-strony__akcje">{akcje}</div>}
    </header>
  );
}

/* ---------- Sekcja ---------- */
export interface SekcjaProps {
  tytul: string;
  akcje?: ReactNode;
  id?: string;
  className?: string;
  children: ReactNode;
}

export function Sekcja({ tytul, akcje, id, className, children }: SekcjaProps) {
  const idTytulu = useId();
  return (
    <section id={id} className={cx("iot-sekcja", className)} aria-labelledby={idTytulu}>
      <div className="iot-sekcja__naglowek"><h2 className="iot-sekcja__tytul" id={idTytulu}>{tytul}</h2>{akcje}</div>
      {children}
    </section>
  );
}

/* ---------- Kafel z liczbą ---------- */
export interface KafelProps {
  etykieta: string;
  /** Wartość sformatowana, np. "2,86" */
  wartosc: string;
  jednostka?: string;
  opis?: ReactNode;
  ikona?: KomponentIkony;
  /** Wartość w kolorze linii */
  akcent?: boolean;
  className?: string;
}

export function Kafel({ etykieta, wartosc, jednostka, opis, ikona: Ikona, akcent, className }: KafelProps) {
  return (
    <div className={cx("iot-kafel", akcent && "iot-kafel--akcent", className)}>
      <span className="iot-kafel__etykieta">{Ikona && <Ikona />}{etykieta}</span>
      <span className="iot-kafel__wartosc"><span key={wartosc} className="iot-kafel__liczba">{wartosc}</span>{jednostka && <span className="iot-kafel__jednostka">{jednostka}</span>}</span>
      {opis && <span className="iot-kafel__opis">{opis}</span>}
    </div>
  );
}

/* ---------- Pigułka statusu ---------- */
export type RodzajPigulki = "zwykla" | "ok" | "uwaga" | "alarm" | "info" | "akcent" | "premium" | "wylaczona";

export interface PigulkaProps {
  rodzaj?: RodzajPigulki;
  ikona?: KomponentIkony;
  /** Kropka statusu; z naZywo=true pulsuje */
  kropka?: boolean;
  naZywo?: boolean;
  mala?: boolean;
  className?: string;
  children: ReactNode;
}

export function Pigulka({ rodzaj = "zwykla", ikona: Ikona, kropka, naZywo, mala, className, children }: PigulkaProps) {
  return (
    <span className={cx("iot-pigulka", rodzaj !== "zwykla" && `iot-pigulka--${rodzaj}`, mala && "iot-pigulka--mala", naZywo && "is-live", className)}>
      {(kropka || naZywo) && <span className="iot-pigulka__kropka" aria-hidden="true" />}
      {Ikona && <Ikona />}
      {children}
    </span>
  );
}

/** Gotowa pigułka „Online / Offline” urządzenia */
export function StanPolaczenia({ online, mala }: { online: boolean; mala?: boolean }) {
  return online
    ? <Pigulka rodzaj="ok" naZywo mala={mala}>{t("Online")}</Pigulka>
    : <Pigulka rodzaj="wylaczona" mala={mala}>{t("Offline")}</Pigulka>;
}

/* ---------- Awatar ---------- */
export interface AwatarProps {
  /** Inicjały, gdy brak zdjęcia */
  inicjaly: string;
  zdjecieUrl?: string;
  rozmiar?: "maly" | "zwykly" | "duzy";
  ikona?: KomponentIkony;
}

export function Awatar({ inicjaly, zdjecieUrl, rozmiar = "zwykly", ikona: Ikona }: AwatarProps) {
  return (
    <span className={cx("iot-awatar", rozmiar !== "zwykly" && `iot-awatar--${rozmiar}`)} aria-hidden="true">
      {zdjecieUrl ? <img src={zdjecieUrl} alt="" /> : Ikona ? <Ikona /> : inicjaly}
    </span>
  );
}

/* ---------- Zdjęcie z danych ---------- */
export interface ZdjecieProps {
  url?: string | null;
  /** Tekst alternatywny; pusty, gdy zdjęcie jest ozdobą */
  opis: string;
  /** Proporcje, np. "1 / 1", "16 / 9" (domyślnie 4 / 3) */
  proporcje?: string;
  /** Kadr: pozycja obrazka, np. "50% 38%" */
  kadr?: string;
  /** Znaczek w lewym górnym rogu */
  znak?: ReactNode;
  /** Przycisk w prawym dolnym rogu */
  akcja?: ReactNode;
  className?: string;
}

export function Zdjecie({ url: adres, opis, proporcje, kadr, znak, akcja, className }: ZdjecieProps) {
  const url = useObrazZaplecza(adres); // aplikacja ze sklepu: obrazek z zaplecza przez żeton (api/zaplecze.ts)
  const style = zmienne({ ...(proporcje ? { "--iot-zdjecie-proporcje": proporcje } : {}), ...(kadr ? { "--iot-zdjecie-kadr": kadr } : {}) });
  return (
    <div className={cx("iot-zdjecie", className)} style={style}>
      {url ? <img className="iot-zdjecie__img" src={url} alt={opis} loading="lazy" /> : <span className="iot-zdjecie__brak"><IkonaObraz /><span className="iot-sr">{t("Brak zdjęcia")}</span></span>}
      {znak && <span className="iot-zdjecie__znak">{znak}</span>}
      {akcja && <span className="iot-zdjecie__akcja">{akcja}</span>}
    </div>
  );
}

/* ---------- Wskazówka ---------- */
export function Wskazowka({ ikona: Ikona, children }: { ikona: KomponentIkony; children: ReactNode }) {
  return <p className="iot-wskazowka"><Ikona />{<span>{children}</span>}</p>;
}

/* ---------- Pasek postępu ---------- */
export interface PostepProps {
  /** 0-100 */
  procent: number;
  etykieta: string;
  cienki?: boolean;
  rodzaj?: "akcent" | "ok" | "uwaga";
  className?: string;
}

export function Postep({ procent, etykieta, cienki, rodzaj = "akcent", className }: PostepProps) {
  const p = Math.min(100, Math.max(0, procent));
  return (
    <div className={cx("iot-postep", cienki && "iot-postep--cienki", rodzaj !== "akcent" && `iot-postep--${rodzaj}`, className)} role="progressbar" aria-label={etykieta} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(p)}>
      <span className="iot-postep__pasek" style={zmienne({ "--iot-postep": `${p}%` })} />
    </div>
  );
}

/* ---------- Oś czasu (etapy, statusy) ---------- */
export interface PunktOsiCzasu {
  id: string;
  tytul: ReactNode;
  opis?: ReactNode;
  stan: "zrobione" | "biezace" | "przyszle";
  /** Pod punktem (APP - zwinięte kroki etapu planu) */
  szczegoly?: ReactNode;
}

/** Oś czasu pakietu; `onPrzelacz` (APP - etapy planu do odhaczania) robi z punktu przycisk „zrobione / nie”. */
export function OsCzasu({ punkty, etykieta, onPrzelacz }: { punkty: ReadonlyArray<PunktOsiCzasu>; etykieta: string; onPrzelacz?: (id: string) => void }) {
  return (
    <ol className="iot-os-czasu" aria-label={etykieta}>
      {punkty.map((p) => (
        <li key={p.id} className={cx("iot-os-czasu__punkt", p.stan === "zrobione" && "is-done", p.stan === "biezace" && "is-current")} aria-current={p.stan === "biezace" ? "step" : undefined}>
          {onPrzelacz
            ? <button type="button" className="iot-os-czasu__przycisk" aria-pressed={p.stan === "zrobione"} onClick={() => onPrzelacz(p.id)}>
                <span className="iot-os-czasu__tytul">{p.tytul}</span>{p.opis && <span className="iot-os-czasu__opis">{p.opis}</span>}
              </button>
            : <><span className="iot-os-czasu__tytul">{p.tytul}</span>{p.opis && <span className="iot-os-czasu__opis">{p.opis}</span>}</>}
          {p.szczegoly && <div className="iot-os-czasu__szczegoly">{p.szczegoly}</div>}
        </li>
      ))}
    </ol>
  );
}

/* ---------- Lista ---------- */
export interface PozycjaListyProps {
  tytul: ReactNode;
  opis?: ReactNode;
  ikona?: KomponentIkony;
  /** Element na początku zamiast ikony (np. Awatar) */
  poczatek?: ReactNode;
  /** Elementy na końcu (pigułka, przyciski) */
  koniec?: ReactNode;
  className?: string;
}

export function Lista({ children, className, etykieta }: { children: ReactNode; className?: string; etykieta?: string }) {
  return <ul className={cx("iot-lista", className)} aria-label={etykieta}>{children}</ul>;
}

export function PozycjaListy({ tytul, opis, ikona: Ikona, poczatek, koniec, className }: PozycjaListyProps) {
  return (
    <li className={cx("iot-lista__pozycja", className)}>
      {poczatek ?? (Ikona && <span className="iot-lista__ikona"><Ikona /></span>)}
      <span className="iot-lista__tresc">
        <span className="iot-lista__tytul">{tytul}</span>
        {opis && <span className="iot-lista__opis">{opis}</span>}
      </span>
      {koniec}
    </li>
  );
}

// Oś czasu, kroki kreatora, stronicowanie, belka akcji - w ./Kroki (limit 300 linii), import dalej przez ./Karta.
export * from "./Kroki";
